import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/stores/authStore';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';

export const apiClient: AxiosInstance = axios.create({
  baseURL: `${API_URL}/api`,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

// ─── Token refresh queue ───────────────────────────────────────────────────
// Prevents multiple simultaneous refresh calls when many requests expire together
let isRefreshing = false;
let refreshQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}> = [];

function drainQueue(token: string | null, err: unknown = null) {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (token) resolve(token);
    else reject(err);
  });
  refreshQueue = [];
}

async function refreshAccessToken(): Promise<string> {
  const { refreshToken, setTokens, logout } = useAuthStore.getState();
  if (!refreshToken) {
    logout();
    throw new Error('No refresh token');
  }

  // Use bare axios (not apiClient) to avoid interceptor loops
  const res = await axios.post(`${API_URL}/api/auth/refresh`, { refreshToken });
  const newAccessToken: string = res.data.data.accessToken;
  // Server may also rotate the refresh token
  const newRefreshToken: string = res.data.data.refreshToken ?? refreshToken;
  setTokens(newAccessToken, newRefreshToken);
  return newAccessToken;
}

// ─── Decode JWT expiry without a library ──────────────────────────────────
function isExpiredOrNearExpiry(token: string): boolean {
  try {
    const [, payloadB64] = token.split('.');
    const { exp } = JSON.parse(atob(payloadB64)) as { exp: number };
    return Date.now() / 1000 >= exp - 60; // refresh 60s before expiry
  } catch {
    return true;
  }
}

// ─── Request interceptor: proactively refresh if token near-expiry ─────────
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const { accessToken } = useAuthStore.getState();

    if (!accessToken) return config;

    if (isExpiredOrNearExpiry(accessToken)) {
      if (isRefreshing) {
        // Queue this request until the ongoing refresh finishes
        const token = await new Promise<string>((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        });
        config.headers.Authorization = `Bearer ${token}`;
        return config;
      }

      isRefreshing = true;
      try {
        const token = await refreshAccessToken();
        drainQueue(token);
        config.headers.Authorization = `Bearer ${token}`;
      } catch (err) {
        drainQueue(null, err);
        useAuthStore.getState().logout();
      } finally {
        isRefreshing = false;
      }
      return config;
    }

    config.headers.Authorization = `Bearer ${accessToken}`;
    return config;
  },
  error => Promise.reject(error),
);

// ─── Response interceptor: handle unexpected 401 (token revoked, etc.) ────
apiClient.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        // Another refresh is already running — queue and retry
        try {
          const token = await new Promise<string>((resolve, reject) => {
            refreshQueue.push({ resolve, reject });
          });
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        } catch (err) {
          return Promise.reject(err);
        }
      }

      isRefreshing = true;
      try {
        const token = await refreshAccessToken();
        drainQueue(token);
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return apiClient(originalRequest);
      } catch (err) {
        drainQueue(null, err);
        useAuthStore.getState().logout();
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export default apiClient;
